import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from '@/hooks/useTranslation';
import { useDeclaredVatPeriod } from '@/hooks/useVatSettlement';

export interface DeclaredVatBannerProps {
  organizationId: string | undefined;
  /** From `declaredSettlementIdOf(entries)` or the index row's `vat_declared_settlement_id`. */
  settlementId?: string | null;
  /**
   * When no line carries a settlement tag: a calendar day inside a filed
   * period (an issued AR invoice's date, `in_filed_vat_period`) — the banner
   * names the live filed return covering it.
   */
  filedOnDate?: string | null;
  /**
   * `tax.declared.banner` (default) tells an editable form which inputs are
   * frozen and why; `tax.declared.readOnlyBanner` is for a document that has
   * no header-only path at all (AR invoices) and opens read-only;
   * `invoices.lifecycle.readOnly.filedPeriod` is an issued AR invoice dated
   * in a filed period with no declared line (`filedOnDate`).
   */
  messageKey?:
    | 'tax.declared.banner'
    | 'tax.declared.readOnlyBanner'
    | 'invoices.lifecycle.readOnly.filedPeriod';
}

/**
 * "VAT on this document was declared on the … return" — the one banner the
 * expense form and the journal modal show above their frozen inputs
 * (docs/TAX.md "Edit guard"). Owns the period lookup (`useDeclaredVatPeriod`,
 * fetched only when `settlementId` is set) so callers pass the id and nothing
 * else.
 *
 * `info`, not `warning`: a declared document is a factual state the user put
 * it in by filing, not a fault (the currency-conversion banners draw the same
 * line). Renders nothing until the period resolves — a sentence with a blank
 * where the period goes reads as broken.
 */
export function DeclaredVatBanner({
  organizationId,
  settlementId = null,
  filedOnDate = null,
  messageKey = 'tax.declared.banner',
}: DeclaredVatBannerProps) {
  const { t } = useTranslation();
  const period = useDeclaredVatPeriod(organizationId, settlementId, filedOnDate);

  if ((!settlementId && !filedOnDate) || !period) return null;

  return (
    <Alert variant="info" data-testid="declared-vat-banner">
      <Info className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{t(messageKey, { period: period.label })}</AlertDescription>
    </Alert>
  );
}
